import _ from 'lodash';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { IEntityConfig } from '../entity-manager/entity-manager.types';
import { Spinner } from '../../base';
import * as S from './universal-claims-search-input.styles';

export interface IUniversalEntitySearchInputProps<T = any> {
    entityConfig: IEntityConfig<T>;
    disabled?: boolean;
    onEntitySelected?: (entityId: string) => Promise<void>;
    onBulkEntitiesSelected?: (entityIds: string[]) => Promise<void>;
}

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const isValidUUID = (str: string): boolean => UUID_REGEX.test(str);

interface IParseResult {
    validIds: string[];
    invalidLines: string[];
}

const parseMultilineIds = (input: string): IParseResult => {
    const lines = input.split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0);

    const validIds: string[] = [];
    const invalidLines: string[] = [];

    lines.forEach(line => {
        if (isValidUUID(line)) {
            validIds.push(line);
        } else {
            invalidLines.push(line);
        }
    });

    return { validIds: _.uniq(validIds), invalidLines };
};

export function UniversalEntitySearchInput<T = any>(
    props: IUniversalEntitySearchInputProps<T>,
): React.ReactElement {
    const { entityConfig, disabled, onEntitySelected, onBulkEntitiesSelected } = props;
    const placeholder = entityConfig.searchPlaceholder || `Search for ${entityConfig.displayNamePlural.toLowerCase()} or paste IDs`;
    
    const [inputValue, setInputValue] = useState<string>('');
    const [searchResults, setSearchResults] = useState<T[]>([]);
    const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
    const [errors, setErrors] = useState<string[]>([]);
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [isSearching, setIsSearching] = useState<boolean>(false);
    const [isSelectingEntity, setIsSelectingEntity] = useState<boolean>(false);
    const [selectedIndex, setSelectedIndex] = useState<number>(-1);
    
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Detect input mode: single-line (search) vs multi-line (bulk)
    const getInputMode = useCallback((): 'search' | 'bulk' => {
        const lineCount = inputValue.split('\n').filter(line => line.trim().length > 0).length;
        return lineCount <= 1 ? 'search' : 'bulk';
    }, [inputValue]);

    // Debounced search for single-line mode
    const performSearch = useCallback(async (term: string) => {
        if (!term || term.trim().length === 0) {
            setSearchResults([]);
            setIsDropdownOpen(false);
            setIsSearching(false);
            return;
        }

        setIsSearching(true);
        setIsDropdownOpen(true); // Show dropdown immediately with loading state
        try {
            const results = await entityConfig.searchFn(term);
            if (results) {
                setSearchResults(results);
                setSelectedIndex(-1);
            }
        } catch (err) {
            console.error('Search error:', err);
            setSearchResults([]);
        } finally {
            setIsSearching(false);
        }
    }, [entityConfig]);

    // Handle input change
    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        setInputValue(value);
        setErrors([]); // Clear errors when typing

        const mode = value.split('\n').filter(line => line.trim().length > 0).length <= 1 ? 'search' : 'bulk';

        if (mode === 'search') {
            // Debounce search
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
            searchTimeoutRef.current = setTimeout(() => {
                performSearch(value.trim());
            }, 300);
        } else {
            // In bulk mode, close dropdown and clear search state
            setIsDropdownOpen(false);
            setSearchResults([]);
            setIsSearching(false);
        }
    }, [performSearch]);

    // Handle blur - process bulk IDs if in bulk mode
    const handleBlur = useCallback(async () => {
        const mode = getInputMode();
        
        if (mode === 'bulk' && inputValue.trim().length > 0) {
            await processBulkIds();
        }
    }, [inputValue, getInputMode]);

    // Process bulk IDs
    const processBulkIds = useCallback(async () => {
        setIsProcessing(true);
        setErrors([]);

        const { validIds, invalidLines } = parseMultilineIds(inputValue);

        const errorMessages: string[] = [];
        const validEntities: T[] = [];
        const notFoundIds: string[] = [];

        // Validate format errors
        if (invalidLines.length > 0) {
            invalidLines.forEach(line => {
                errorMessages.push(`"${line}" - invalid UUID format`);
            });
        }

        // Fetch entities for valid IDs using bulk API call
        if (validIds.length > 0) {
            try {
                // Fetch all entities in a single API call
                const fetchedEntities = await entityConfig.fetchEntitiesFn(validIds);
                
                // Create a map of fetched entities by ID for quick lookup
                const fetchedEntitiesMap = new Map(
                    fetchedEntities
                        .map(entity => {
                            const id = entityConfig.getEntityId(entity);
                            return id ? [id, entity] : null;
                        })
                        .filter(Boolean) as [string, T][]
                );
                
                // Check which IDs were found and which were not
                validIds.forEach(id => {
                    const entity = fetchedEntitiesMap.get(id);
                    if (entity) {
                        validEntities.push(entity);
                    } else {
                        notFoundIds.push(id);
                    }
                });

                // Add not found errors
                notFoundIds.forEach(id => {
                    errorMessages.push(`"${id}" - ${entityConfig.displayName.toLowerCase()} not found`);
                });

            } catch (err) {
                console.error('Bulk validation error:', err);
                errorMessages.push(`Error validating ${entityConfig.displayNamePlural.toLowerCase()}. Please try again.`);
            }
        }

        setIsProcessing(false);

        // Show errors if any
        if (errorMessages.length > 0) {
            setErrors(errorMessages);
        }

        if (validEntities.length > 0 && onBulkEntitiesSelected) {
            const validEntityIds = _.compact(validEntities.map(e => entityConfig.getEntityId(e)));
            await onBulkEntitiesSelected(validEntityIds);
            
            // Remove valid IDs from textarea, keep invalid ones
            if (errorMessages.length > 0) {
                const remainingLines = inputValue.split('\n')
                    .map(line => line.trim())
                    .filter(line => {
                        if (line.length === 0) return false;
                        // Keep invalid lines
                        return invalidLines.includes(line) || notFoundIds.includes(line);
                    });
                setInputValue(remainingLines.join('\n'));
            } else {
                // Clear input if all valid
                setInputValue('');
            }
        }
    }, [inputValue, entityConfig, onBulkEntitiesSelected]);

    const handleEntitySelect = useCallback(async (entity: T) => {
        if (isSelectingEntity) {
            return; // Prevent multiple simultaneous selections
        }
        
        const entityId = entityConfig.getEntityId(entity);
        if (entityId && onEntitySelected) {
            setIsSelectingEntity(true);
            try {
                await onEntitySelected(entityId);
                setInputValue('');
                setSearchResults([]);
                setIsDropdownOpen(false);
                setIsSearching(false);
                setSelectedIndex(-1);
            } finally {
                setIsSelectingEntity(false);
            }
        }
    }, [onEntitySelected, isSelectingEntity, entityConfig]);

    // Handle keyboard navigation
    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (getInputMode() !== 'search' || !isDropdownOpen || searchResults.length === 0 || isSearching || isSelectingEntity) {
            return;
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setSelectedIndex(prev => 
                    prev < searchResults.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
                break;
            case 'Enter':
                e.preventDefault();
                if (selectedIndex >= 0 && selectedIndex < searchResults.length) {
                    handleEntitySelect(searchResults[selectedIndex]);
                }
                break;
            case 'Escape':
                e.preventDefault();
                setIsDropdownOpen(false);
                setSelectedIndex(-1);
                break;
        }
    }, [getInputMode, isDropdownOpen, searchResults, selectedIndex, isSearching, isSelectingEntity, handleEntitySelect]);

    // Auto-resize textarea
    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            const scrollHeight = textareaRef.current.scrollHeight;
            const maxHeight = 10 * 24; // 10 rows * approximate line height
            textareaRef.current.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
        }
    }, [inputValue]);

    // Click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (
                dropdownRef.current &&
                textareaRef.current &&
                !dropdownRef.current.contains(e.target as Node) &&
                !textareaRef.current.contains(e.target as Node)
            ) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Render search results using custom rows or columns
    const renderSearchResults = () => {
        if (!isDropdownOpen) return null;

        if (isSearching) {
            return (
                <S.Dropdown ref={dropdownRef}>
                    <S.LoadingMessage>
                        <Spinner />
                        <span style={{ marginLeft: '8px' }}>Searching...</span>
                    </S.LoadingMessage>
                </S.Dropdown>
            );
        }

        if (isSelectingEntity) {
            return (
                <S.Dropdown ref={dropdownRef}>
                    <S.LoadingMessage>
                        <Spinner />
                        <span style={{ marginLeft: '8px' }}>Loading {entityConfig.displayName.toLowerCase()}...</span>
                    </S.LoadingMessage>
                </S.Dropdown>
            );
        }

        if (searchResults.length === 0) return null;

        return (
            <S.Dropdown ref={dropdownRef}>
                {searchResults.map((entity, index) => {
                    const entityId = entityConfig.getEntityId(entity);
                    
                    if (entityConfig.getDropdownRows) {
                        const rows = entityConfig.getDropdownRows(entity);
                        return (
                            <S.SearchResult 
                                key={entityId || index} 
                                isSelected={index === selectedIndex}
                                onClick={() => handleEntitySelect(entity)}
                            >
                                {rows.map((cells, rowIndex) => (
                                    <S.SearchResultRow key={rowIndex}>
                                        {cells.map((cell, cellIndex) => (
                                            <S.SearchResultCell key={cellIndex}>{cell}</S.SearchResultCell>
                                        ))}
                                    </S.SearchResultRow>
                                ))}
                            </S.SearchResult>
                        );
                    }
                    
                    // Otherwise, use columns configuration
                    const columns = entityConfig.dropdownColumns || entityConfig.columns;
                    return (
                        <S.SearchResult 
                            key={entityId || index} 
                            isSelected={index === selectedIndex}
                            onClick={() => handleEntitySelect(entity)}
                        >
                            <S.SearchResultRow>
                                {columns.map(column => (
                                    <S.SearchResultCell key={column.key}>
                                        {column.render 
                                            ? column.render(entity)
                                            : column.accessor
                                                ? column.accessor(entity)
                                                : '-'}
                                    </S.SearchResultCell>
                                ))}
                            </S.SearchResultRow>
                        </S.SearchResult>
                    );
                })}
            </S.Dropdown>
        );
    };

    // Render errors
    const renderErrors = () => {
        if (errors.length === 0) return null;

        return (
            <S.ErrorContainer>
                <S.ErrorTitle>Invalid {entityConfig.displayName.toLowerCase()} IDs:</S.ErrorTitle>
                <S.ErrorList>
                    {errors.map((error, index) => (
                        <S.ErrorItem key={index}>{error}</S.ErrorItem>
                    ))}
                </S.ErrorList>
            </S.ErrorContainer>
        );
    };

    return (
        <S.Container>
            <S.TextareaWrapper>
                <S.Textarea
                    ref={textareaRef}
                    value={inputValue}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    disabled={disabled || isProcessing || isSelectingEntity}
                    rows={1}
                />
                {isProcessing && (
                    <S.LoadingOverlay>
                        <Spinner />
                    </S.LoadingOverlay>
                )}
            </S.TextareaWrapper>
            {renderSearchResults()}
            {renderErrors()}
        </S.Container>
    );
}

// Backward compatibility export
export const UniversalClaimsSearchInput = UniversalEntitySearchInput;

// Legacy props interface for backward compatibility
export interface IUniversalClaimsSearchInputProps {
    disabled?: boolean;
    placeholder?: string;
    onClaimSelected?: (claimId: string) => Promise<void>;
    onBulkClaimsSelected?: (claimIds: string[]) => Promise<void>;
}
